import { useEffect, useState, useSyncExternalStore } from 'react';
import analyticsService from './Analytics';
import { FeatureFlag } from './featureFlags';

// Handed to useSyncExternalStore below. It lives out here rather than inline
// because React re-subscribes whenever this function changes identity, and an
// inline arrow would be a brand new function on every render.
const subscribe = (listener: () => void): (() => void) =>
  analyticsService.subscribeToFlags(listener);

/**
 * Reads a feature flag, and re-renders the component when its value changes.
 *
 * WHY THIS EXISTS
 * Flag values arrive from CleverTap over the network, and whether they land
 * before or after the screen is drawn is a race — nobody controls which side
 * wins. Reading the flag directly returns whatever the SDK happens to be
 * holding at that instant, so the very same code shows the variant on a fast
 * connection and control on a slow one.
 *
 * A direct read also re-runs on every render, so when the value lands late the
 * text can change at some arbitrary later moment — when the user taps the
 * monthly/yearly toggle, picks a plan, or any unrelated state update repaints
 * the screen. The price appears to change while they are looking at it.
 *
 * Neither behaviour is acceptable for an experiment. Which arm a user
 * experiences would come down to their connection speed, and connection speed
 * tracks device, country and income — the same things that drive whether they
 * buy at all. That quietly corrupts the result. It is also invisible in QA,
 * where the office network wins the race every time.
 *
 * This hook removes the race: whenever the value lands, the screen matches it.
 *
 * WHAT useSyncExternalStore DOES
 * It is React's built-in hook for reading data that lives *outside* React — in
 * our case inside the CleverTap SDK — and keeping the component in sync with
 * it. You give it two functions:
 *
 *   1. subscribe(listener)  "call this listener whenever the data changes"
 *   2. getSnapshot()        "what is the value right now?"
 *
 * React calls getSnapshot and renders what it returns. Later, when the listener
 * fires, React calls getSnapshot again — and if the value is different from
 * last time, it re-renders the component with the new one.
 *
 * SO THE SEQUENCE IS
 *   first paint       → if nothing has landed yet, renders the local default
 *   fetch lands       → the adapter notifies its listeners
 *   React re-renders  → getSnapshot now returns the real value
 * When the fetch wins the race the value is simply correct on the first paint
 * and there is nothing to update.
 *
 * If the flag is unset or hasn't resolved, this falls back to the flag's own
 * local default, so a screen always has something sensible to show.
 *
 * Takes the registry constant rather than a name — `useFlag(FEATURE_FLAG.X)` —
 * so the string that has to match the CleverTap dashboard never appears at a
 * call site.
 */
export function useFlag(flag: FeatureFlag): string {
  // getSnapshot has to return something React can compare cheaply between
  // calls. A string is perfect — two equal strings are already ===. Returning a
  // fresh object here instead would look "changed" every single time and send
  // React into an infinite re-render loop.
  return useSyncExternalStore(subscribe, () =>
    String(analyticsService.fetchVariant(flag.name)?.value || flag.defaultValue)
  );
}

/**
 * Whether flag values for the identified user have landed: 'ready' once they
 * have, 'timeout' if `timeoutMs` passed first, 'waiting' until then.
 *
 * useFlag() above is right for anything that may change after first paint.
 * A whole-page variant may not: the anonymous boot fetch and the post-login
 * fetch can disagree, and swapping the page between them would show the user
 * two different screens. Screens like that wait on this before reading the
 * flag once. The timeout keeps a slow or dead network from blocking the page;
 * callers decide what a timeout means (ExperimentRoute shows control and keeps
 * the user out of the experiment).
 *
 * The clock only starts once `startTimer` is true. In the webview, AppBridge
 * opens the screen before the session exists, and on a first open the user is
 * only identified after it — counting from mount would spend the timeout on
 * session setup and time out users CleverTap was about to answer for.
 */
export function useFlagsStatus(
  timeoutMs: number,
  startTimer: boolean
): 'waiting' | 'ready' | 'timeout' {
  const ready = useSyncExternalStore(subscribe, () =>
    analyticsService.isFlagsReady()
  );
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (ready || !startTimer) return undefined;
    const id = setTimeout(() => setTimedOut(true), timeoutMs);
    return () => clearTimeout(id);
  }, [ready, startTimer, timeoutMs]);

  if (ready) return 'ready';
  return timedOut ? 'timeout' : 'waiting';
}
